import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { Events } from '@scale/events';
import { formatMoney } from '@scale/currency';
import { fetchTextWithTimeout, getCartSections } from '@scale/utilities';

const HAS_BUNDLE = '[data-bundle-contributor]';

class ProductForm extends ScaleElement {
  #submitting = false;
  #addedTimer;
  #currentVariant;
  #notifySubmitting = false;
  #notifySwap = 0;

  get #layout() {
    return this.closest('.product-page') ?? this;
  }

  setup() {
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', (e) => this.#handleSubmit(e), { signal: this.signal });

    this.#currentVariant = this.#initialVariant();
    this.#fillPaymentButtonSlots();

    this.onVariantSelected((e) => this.#onVariantSelected(e.variant));
    this.onDocument(Events.BUNDLE_UPDATED, () => this.#refreshAtcPrice());
    this.onDocument(Events.QUANTITY_BREAKS_UPDATED, () => this.#refreshAtcPrice());

    this.#setupNotifyForm();

    requestAnimationFrame(() => this.#refreshAtcPrice());
  }

  #fillPaymentButtonSlots() {
    const button = this.ref('payment-button');
    if (!button) return;
    for (const slot of this.querySelectorAll('[data-payment-button-slot]:empty')) {
      slot.append(button.content.cloneNode(true));
    }
  }

  #initialVariant() {
    const variants = this.#variants();
    const id = this.ref('variant-id')?.value;
    return variants.find((v) => String(v.id) === String(id)) ?? variants[0] ?? null;
  }

  #variants() {
    const json = this.querySelector('[data-product-variants], [data-ref="variants-json"]');
    return json ? JSON.parse(json.textContent) : [];
  }

  async #handleSubmit(e) {
    e.preventDefault();
    if (this.#submitting) return;

    const form = e.target;
    const errorEl = this.ref('error');
    const errorMsg = this.ref('error-message');
    if (errorEl) errorEl.hidden = true;

    const variantInput = this.ref('variant-id');
    if (!variantInput || variantInput.disabled) return;
    if (this.#layout.querySelector(`${HAS_BUNDLE}[data-bundle-blocked="true"]`)) return;
    const invalid = [...form.elements].find((field) => field.name?.startsWith('properties[') && !field.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      return;
    }

    this.#submitting = true;
    const status = this.ref('status');
    clearTimeout(this.#addedTimer);
    this.#showAddState('loading');

    try {
      await this.#prepareBundleContributions();

      const formData = new FormData(form);
      let items = [{
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1,
        properties: this.#extractProperties(formData),
      }];

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items[0].selling_plan = Number(sellingPlan);
      items = await this.#applyBundleContributions(items);

      const cart = await CartAPI.add(items, {
        sections: getCartSections(),
        target: this,
        context: this.closest('dialog') ? 'dialog' : 'product',
      });

      if (status) status.textContent = Scale.translations.addedToCart;
      this.announce(Scale.translations.addedToCart);

      this.#showAddState('added');
      this.#addedTimer = setTimeout(() => this.#showAddState(null), 1500);
    } catch (err) {
      this.#showAddState(null);
      if (errorEl && errorMsg) {
        errorMsg.textContent = err.message;
        errorEl.hidden = false;
      }
      if (status) status.textContent = err.message;
    } finally {
      this.#submitting = false;
    }
  }

  #showAddState(state) {
    const button = this.ref('add-to-cart');
    if (button) {
      if (state) button.dataset.state = state;
      else delete button.dataset.state;
      if (state === 'loading') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
    this.emit('product-form:adding', { state });
  }

  get purchase() {
    return this.#currentVariant && { variant: this.#currentVariant, ...this.#computeTotal() };
  }

  #onVariantSelected(variant) {
    this.#currentVariant = variant;

    const addButton = this.ref('add-to-cart');
    const label = this.ref('atc-label');
    const variantInput = this.ref('variant-id');
    const status = this.ref('status');

    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    if (addButton) {
      addButton.disabled = !variant.available
        || addButton.dataset.bundleConflict === 'true'
        || addButton.dataset.variantBundleDisabled === 'true';
      addButton.dataset.variantAvailable = String(variant.available);
      this.#replayShine(addButton, variant.available);
    }

    const labelText = variant.available
      ? addButton?.dataset.labelAvailable ?? Scale.translations.addToCart
      : Scale.translations.soldOut;

    if (label) label.textContent = labelText;

    if (status) {
      status.textContent = variant.available
        ? `${labelText} ${formatMoney(variant.price)}`
        : labelText;
    }

    this.#refreshAtcPrice();
    this.#toggleNotify(variant);
  }

  #replayShine(button, available) {
    if (button.dataset.animate !== 'shine') return;
    button.classList.remove('animating');
    if (!available) return;
    button.offsetWidth;
    button.classList.add('animating');
    const settle = (event) => {
      if (event.animationName !== 'atc-shine') return;
      button.classList.remove('animating');
      button.removeEventListener('animationend', settle);
    };
    button.addEventListener('animationend', settle);
  }

  #computeTotal() {
    const variant = this.#currentVariant;
    if (!variant) return null;

    const replacements = [];
    let additiveTotal = 0;
    let additiveCompare = 0;

    for (const el of this.#layout.querySelectorAll(HAS_BUNDLE)) {
      const contribution = el.getContributionTotal?.();
      if (!contribution) continue;
      const contribCompare = contribution.compare ?? contribution.total;
      if (contribution.replace) {
        replacements.push({ total: contribution.total, compare: contribCompare });
      } else {
        additiveTotal += contribution.total;
        additiveCompare += contribCompare;
      }
    }

    const main = replacements[0] ?? {
      total: variant.price,
      compare: variant.compare_at_price || 0,
    };

    return {
      total: main.total + additiveTotal,
      compare: main.compare + additiveCompare,
      conflict: replacements.length > 1,
    };
  }

  #refreshAtcPrice() {
    const atcPrice = this.ref('atc-price');
    const variant = this.#currentVariant;
    if (!variant) return;

    if (!variant.available) {
      if (atcPrice) atcPrice.textContent = '';
      this.emit('product-form:purchase', this.purchase);
      return;
    }

    const { total, conflict } = this.#computeTotal();
    if (atcPrice) atcPrice.textContent = `— ${formatMoney(total)}`;

    const addButton = this.ref('add-to-cart');
    const status = this.ref('status');
    if (conflict) {
      if (addButton) {
        addButton.disabled = true;
        addButton.dataset.bundleConflict = 'true';
      }
      if (status) status.textContent = Scale.translations.bundleConflict;
    } else if (addButton?.dataset.bundleConflict === 'true') {
      delete addButton.dataset.bundleConflict;
      addButton.disabled = !variant.available
        || addButton.dataset.variantBundleDisabled === 'true';
      if (status) status.textContent = '';
    }

    this.emit('product-form:purchase', this.purchase);
  }

  async #applyBundleContributions(items) {
    const contributors = this.#layout.querySelectorAll(HAS_BUNDLE);
    if (!contributors.length) return items;
    const contributions = [...contributors].map((element) => element.getCartContribution?.());
    const { applyCartContributions } = await import('@scale/bundle-utils');
    return applyCartContributions(items, contributions);
  }

  async #prepareBundleContributions() {
    const contributors = [...this.#layout.querySelectorAll(HAS_BUNDLE)];
    await Promise.all(contributors.map((element) => element.prepareCartContribution?.()));
  }

  #extractProperties(formData) {
    const props = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        props[key.replace('properties[', '').replace(']', '')] = value;
      }
    }
    return Object.keys(props).length ? props : undefined;
  }


  #setupNotifyForm() {
    const notifyForm = this.ref('notify-form');
    if (!notifyForm) return;

    const submitBtn = this.ref('notify-submit');
    const emailInput = this.ref('notify-email');

    submitBtn?.addEventListener('click', () => this.#handleNotifySubmit(), { signal: this.signal });
    emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.#handleNotifySubmit();
      }
    }, { signal: this.signal });
  }

  #toggleNotify(variant) {
    const atcWrapper = this.ref('atc-wrapper');
    const notifyWrapper = this.ref('notify-wrapper');
    if (!notifyWrapper) return;

    const notifyVariantInput = this.ref('notify-variant-id');
    if (notifyVariantInput) notifyVariantInput.value = variant.id;

    const run = ++this.#notifySwap;
    for (const wrapper of [atcWrapper, notifyWrapper]) {
      wrapper?.getAnimations({ subtree: true }).filter((animation) => animation.id === 'notify-swap').forEach((animation) => animation.cancel());
    }

    const soldOut = !variant.available;
    if (notifyWrapper.hidden !== soldOut) return;
    const [leaving, arriving] = soldOut ? [atcWrapper, notifyWrapper] : [notifyWrapper, atcWrapper];
    const swap = () => {
      if (atcWrapper) atcWrapper.hidden = soldOut;
      notifyWrapper.hidden = !soldOut;
    };
    const motion = getComputedStyle(notifyWrapper);
    const duration = (name) => parseFloat(motion.getPropertyValue(name));
    const timing = (options) => ({ id: 'notify-swap', ...options });

    if (!leaving || !matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      swap();
      arriving?.animate({ opacity: [0, 1] }, timing({ duration: duration('--duration-normal') }));
      return;
    }

    const from = leaving.offsetHeight;
    leaving.animate([{ opacity: 1, translate: '0 0' }, { opacity: 0, translate: '0 -6px' }], timing({
      duration: duration('--duration-fast'),
      easing: motion.getPropertyValue('--ease-in'),
      fill: 'forwards',
    })).onfinish = () => {
      if (run !== this.#notifySwap) return;
      swap();
      leaving.getAnimations().filter((animation) => animation.id === 'notify-swap').forEach((animation) => animation.cancel());
      arriving.animate([{ blockSize: `${from}px`, overflow: 'clip' }, { blockSize: `${arriving.offsetHeight}px`, overflow: 'clip' }], timing({
        duration: duration('--duration-slow'),
        easing: motion.getPropertyValue('--ease-spring'),
      }));
      [...arriving.children].filter((child) => !child.hidden).forEach((child, index) => {
        child.animate([{ opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }], timing({
          duration: duration('--duration-normal'),
          easing: motion.getPropertyValue('--ease-spring'),
          delay: 40 + index * 50,
          fill: 'backwards',
        }));
      });
    };
  }

  async #handleNotifySubmit() {
    if (this.#notifySubmitting) return;

    const emailInput = this.ref('notify-email');
    if (!emailInput?.checkValidity()) {
      emailInput?.reportValidity();
      return;
    }
    const email = emailInput.value.trim();

    const variantId = this.ref('notify-variant-id')?.value;
    const btn = this.ref('notify-submit');
    const form = this.ref('notify-form');
    this.#notifySubmitting = true;
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    try {
      const { response, text: html } = await fetchTextWithTimeout(`${window.Shopify.routes.root}contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form_type': 'customer',
          'contact[email]': email,
          'contact[tags]': 'back-in-stock',
          'contact[note]': `Variant: ${variantId}`,
        }),
        signal: this.signal,
      });
      if (!this.#notifyResponseSucceeded(response, html)) throw new Error('Notify request rejected');

      const success = this.ref('notify-success');
      if (success) success.hidden = false;
      if (form) form.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.announce(this.dataset.notifyError, 'assertive');
    } finally {
      this.#notifySubmitting = false;
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    }
  }

  #notifyResponseSucceeded(response, html) {
    if (!response.ok) return false;
    const document = new DOMParser().parseFromString(html, 'text/html');
    if (document.querySelector('.errors, [data-form-error], [aria-invalid="true"]')) return false;

    return new URL(response.url).searchParams.get('customer_posted') === 'true';
  }

}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
